import { throttle } from 'lodash-es'
import { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router'
import { z } from 'zod'
import {
  createTypedSessionStorage,
  type TypedSessionStorage,
} from '#src/utils/TypedSessionStorage.ts'

const scrollPositionsSchema = z.record(z.string(), z.number().optional())

type ScrollPositions = z.infer<typeof scrollPositionsSchema>

interface UseScrollRestorationBaseResult {
  storage: TypedSessionStorage<ScrollPositions> | undefined
  locationKey: string
  elementSelector: string | undefined
}

export function useScrollRestorationBase(
  id: string | undefined | null,
  disableScrollRestoration?: boolean
): UseScrollRestorationBaseResult {
  const location = useLocation()
  const locationKey = location.pathname

  const elementSelector = id
    ? `[data-scroll-restoration-id="${id}"]`
    : undefined

  const storage = useMemo(() => {
    if (!elementSelector) {
      return undefined
    }
    return createTypedSessionStorage<ScrollPositions>(
      elementSelector,
      scrollPositionsSchema
    )
  }, [elementSelector])

  useEffect(() => {
    if (!elementSelector || !storage || disableScrollRestoration) {
      return
    }

    const element = document.querySelector(elementSelector)
    if (!element) {
      return
    }

    const savePosition = (): void => {
      const positions = storage.get() ?? {}
      positions[locationKey] = element.scrollTop
      storage.set(positions)
    }

    const ac = new AbortController()

    // Throttle the scroll event to avoid excessive updates
    element.addEventListener('scroll', throttle(savePosition, 100), {
      signal: ac.signal,
    })

    window.addEventListener('pagehide', savePosition, {
      signal: ac.signal,
    })

    return () => {
      ac.abort()
    }
  }, [elementSelector, locationKey, storage, disableScrollRestoration])

  return { storage, locationKey, elementSelector }
}
